import type { WebGLRenderer } from 'three'
import { getLoggerManager } from '@/shared/logging'
import type {
  GSplatAutoDeviceBudgetRecommendations,
  GSplatDeviceBudgetAppliedFields,
  GSplatDeviceBudgetBrowser,
  GSplatDeviceBudgetClampReason,
  GSplatDeviceBudgetDeviceInfo,
  GSplatDeviceBudgetDiagnostics,
  GSplatDeviceBudgetGpuVendor,
  GSplatDeviceBudgetOS,
  GSplatDeviceBudgetSource,
  LODConfig,
} from '@/shared/types/gsplat'

const log = getLoggerManager().getLogger('gsplat:utils')

const AUTO_FALLBACK_BUDGET = 3_000_000
const AUTO_MOBILE_BUDGET = 1_000_000
const AUTO_WINDOWS_UNKNOWN_GPU_BUDGET = 2_000_000
const AUTO_MACOS_BUDGET = 2_500_000
const DEFAULT_TEXTURE_PIXELS_PER_SPLAT = 4
const LOW_UPLOAD_BYTES = 4 * 1024 * 1024
const DEFAULT_UPLOAD_BYTES = 30 * 1024 * 1024

export interface GSplatDeviceBudgetResolveResult {
  lodConfig: LODConfig
  diagnostics: GSplatDeviceBudgetDiagnostics
}

interface AutoTierResult {
  budget: number
  source: Exclude<GSplatDeviceBudgetSource, 'disabled' | 'explicit'>
  branch: string
}

interface AutoDerivedResult {
  appliedFields: GSplatDeviceBudgetAppliedFields
  skippedFields: string[]
  recommendations: GSplatAutoDeviceBudgetRecommendations
}

export function resolveGSplatDeviceBudgetConfig(
  defaults: LODConfig,
  rawConfig: Partial<LODConfig> | undefined,
  renderer?: WebGLRenderer
): GSplatDeviceBudgetResolveResult {
  const raw = rawConfig ?? {}
  const mergedConfig = mergeDefined(defaults, raw)
  const detectedInfo = detectGSplatDeviceBudgetDeviceInfo(renderer)
  const deviceInfo = mergeDefined(detectedInfo, raw.deviceBudget?.deviceInfo ?? {})
  const mode = raw.deviceBudget?.mode ?? 'unset'

  if (mode !== 'auto') {
    const lodConfig = {
      ...mergedConfig,
      deviceBudget: raw.deviceBudget,
    }

    return {
      lodConfig,
      diagnostics: createDisabledDiagnostics(lodConfig, deviceInfo, mode),
    }
  }

  const texturePixelsPerSplat = normalizeTexturePixelsPerSplat(
    raw.deviceBudget?.texturePixelsPerSplat
  )
  const explicitBudget = hasExplicitNumber(raw, 'maxCachePoints') ? raw.maxCachePoints! : null
  const tier = resolveAutoTier(deviceInfo)
  const requestedBudget = explicitBudget ?? tier.budget
  const hardLimit = calculateTextureHardLimit(deviceInfo.maxTextureSize, texturePixelsPerSplat)
  const effectiveBudget =
    hardLimit === null ? requestedBudget : Math.min(requestedBudget, hardLimit)
  const clampReason: GSplatDeviceBudgetClampReason =
    hardLimit === null
      ? 'hard-limit-unavailable'
      : effectiveBudget < requestedBudget
        ? 'texture-hard-limit'
        : 'none'
  const budgetSource: GSplatDeviceBudgetSource = explicitBudget === null ? tier.source : 'explicit'

  const derived = resolveAutoDerivedFields(raw, deviceInfo)
  const lodConfig = {
    ...mergedConfig,
    ...derived.appliedFields,
    maxCachePoints: Math.max(1, Math.floor(effectiveBudget)),
    deviceBudget: raw.deviceBudget,
  }

  return {
    lodConfig,
    diagnostics: {
      enabled: true,
      mode: 'auto',
      deviceInfo,
      texturePixelsPerSplat,
      explicitBudget,
      tierBudget: tier.budget,
      requestedBudget,
      hardLimit,
      effectiveBudget: lodConfig.maxCachePoints,
      budgetSource,
      clampReason,
      autoBranch: tier.branch,
      appliedDerivedFields: derived.appliedFields,
      skippedDerivedFields: derived.skippedFields,
      recommendations: derived.recommendations,
    },
  }
}

export function detectGSplatDeviceBudgetDeviceInfo(
  renderer?: WebGLRenderer
): GSplatDeviceBudgetDeviceInfo {
  const navigatorLike = getNavigatorLike()
  const userAgent = navigatorLike?.userAgent ?? ''
  const gpuRenderer = detectGpuRenderer(renderer)

  const deviceInfo: GSplatDeviceBudgetDeviceInfo = {
    os: detectOS(userAgent),
    browser: detectBrowser(userAgent),
    isFirefox: /firefox/i.test(userAgent),
    isMobile: /iphone|ipad|ipod|android|mobile/i.test(userAgent),
    cpuCores: normalizePositiveInteger(navigatorLike?.hardwareConcurrency),
    gpuVendor: detectGpuVendor(gpuRenderer),
    gpuModel: detectGpuModel(gpuRenderer),
    gpuRenderer,
    maxTextureSize: detectMaxTextureSize(renderer),
  }

  log.debug(
    '[GSplatDeviceBudget] 设备检测: os=' +
      deviceInfo.os +
      ', browser=' +
      deviceInfo.browser +
      ', mobile=' +
      deviceInfo.isMobile +
      ', cpuCores=' +
      deviceInfo.cpuCores +
      ', gpuVendor=' +
      deviceInfo.gpuVendor +
      ', gpuModel=' +
      deviceInfo.gpuModel +
      ', maxTextureSize=' +
      deviceInfo.maxTextureSize +
      ', gpuRenderer=' +
      deviceInfo.gpuRenderer
  )

  return deviceInfo
}

export function calculateTextureHardLimit(
  maxTextureSize: number | null | undefined,
  texturePixelsPerSplat = DEFAULT_TEXTURE_PIXELS_PER_SPLAT
): number | null {
  const textureSize = normalizePositiveInteger(maxTextureSize)
  const pixelsPerSplat = normalizeTexturePixelsPerSplat(texturePixelsPerSplat)
  if (textureSize === null) {
    return null
  }
  return Math.floor((textureSize * textureSize) / pixelsPerSplat)
}

export function cloneGSplatDeviceBudgetDiagnostics(
  diagnostics: GSplatDeviceBudgetDiagnostics
): GSplatDeviceBudgetDiagnostics {
  return {
    ...diagnostics,
    deviceInfo: { ...diagnostics.deviceInfo },
    appliedDerivedFields: { ...diagnostics.appliedDerivedFields },
    skippedDerivedFields: [...diagnostics.skippedDerivedFields],
    recommendations: { ...diagnostics.recommendations },
  }
}

function createDisabledDiagnostics(
  lodConfig: LODConfig,
  deviceInfo: GSplatDeviceBudgetDeviceInfo,
  mode: 'off' | 'unset'
): GSplatDeviceBudgetDiagnostics {
  return {
    enabled: false,
    mode,
    deviceInfo,
    texturePixelsPerSplat: DEFAULT_TEXTURE_PIXELS_PER_SPLAT,
    explicitBudget: null,
    tierBudget: null,
    requestedBudget: lodConfig.maxCachePoints,
    hardLimit: null,
    effectiveBudget: lodConfig.maxCachePoints,
    budgetSource: 'disabled',
    clampReason: 'disabled',
    autoBranch: null,
    appliedDerivedFields: {},
    skippedDerivedFields: [],
    recommendations: {},
  }
}

function resolveAutoTier(deviceInfo: GSplatDeviceBudgetDeviceInfo): AutoTierResult {
  if (deviceInfo.isMobile || deviceInfo.os === 'ios' || deviceInfo.os === 'android') {
    return { budget: AUTO_MOBILE_BUDGET, source: 'auto-tier', branch: 'mobile' }
  }

  if (deviceInfo.os === 'macos') {
    return { budget: AUTO_MACOS_BUDGET, source: 'auto-tier', branch: 'macos' }
  }

  if (deviceInfo.os === 'windows') {
    if (deviceInfo.gpuVendor === 'unknown') {
      return {
        budget: AUTO_WINDOWS_UNKNOWN_GPU_BUDGET,
        source: 'auto-tier',
        branch: 'windows-unknown-gpu',
      }
    }

    if (deviceInfo.isFirefox) {
      return {
        budget: AUTO_FALLBACK_BUDGET,
        source: 'auto-fallback',
        branch: 'windows-firefox-fallback',
      }
    }

    if (deviceInfo.gpuVendor === 'nvidia' && typeof deviceInfo.gpuModel === 'number') {
      if (deviceInfo.gpuModel < 1050) {
        return { budget: 1_000_000, source: 'auto-tier', branch: 'windows-nvidia-low' }
      }
      if (deviceInfo.gpuModel < 2060) {
        return { budget: 2_000_000, source: 'auto-tier', branch: 'windows-nvidia-mid' }
      }
      return {
        budget: AUTO_FALLBACK_BUDGET,
        source: 'auto-fallback',
        branch: 'windows-nvidia-high-fallback',
      }
    }

    if (deviceInfo.gpuVendor === 'amd' && typeof deviceInfo.gpuModel === 'number') {
      if (deviceInfo.gpuModel < 560) {
        return { budget: 1_000_000, source: 'auto-tier', branch: 'windows-amd-low' }
      }
      if (deviceInfo.gpuModel < 5600) {
        return { budget: 2_000_000, source: 'auto-tier', branch: 'windows-amd-mid' }
      }
      return {
        budget: AUTO_FALLBACK_BUDGET,
        source: 'auto-fallback',
        branch: 'windows-amd-high-fallback',
      }
    }
  }

  return { budget: AUTO_FALLBACK_BUDGET, source: 'auto-fallback', branch: 'desktop-fallback' }
}

function resolveAutoDerivedFields(
  rawConfig: Partial<LODConfig>,
  deviceInfo: GSplatDeviceBudgetDeviceInfo
): AutoDerivedResult {
  const appliedFields: GSplatDeviceBudgetAppliedFields = {}
  const skippedFields: string[] = []
  const recommendations: GSplatAutoDeviceBudgetRecommendations = {}
  const isMobile = deviceInfo.isMobile || deviceInfo.os === 'ios' || deviceInfo.os === 'android'
  const isLowEnd =
    !isMobile &&
    ((typeof deviceInfo.cpuCores === 'number' && deviceInfo.cpuCores < 9) ||
      deviceInfo.gpuVendor === 'unknown')

  if (isMobile) {
    recommendations.MaxLodDistance = 30
    recommendations.LodLevelUpSpatsInNode = 500_000
    recommendations.CpuSortThreadNum = 2
    recommendations.MinLodUsed = 1
    recommendations.MaxDistaneLimit = 100
    recommendations.GpuMaxUploadBytes = LOW_UPLOAD_BYTES
    recommendations.GpuMaxUploadCount = 30
    applyDerivedField(rawConfig, appliedFields, skippedFields, 'maxConcurrentLoads', 2)
    applyDerivedField(rawConfig, appliedFields, skippedFields, 'maxRenderDistance', 100)
  } else if (isLowEnd) {
    recommendations.LodLevelUpSpatsInNode = 900_000
    recommendations.GpuMaxUploadBytes = LOW_UPLOAD_BYTES
    recommendations.GpuMaxUploadCount = 30
  } else {
    recommendations.LodLevelUpSpatsInNode = 2_000_000
    recommendations.GpuMaxUploadBytes = DEFAULT_UPLOAD_BYTES
    recommendations.GpuMaxUploadCount = 100
  }

  if (rawConfig.deviceBudget?.applyUploadTuning === false) {
    skippedFields.push(
      'maxImmediateOrderUploadBytes',
      'maxOrderUploadBytesPerFrame',
      'maxOrderUploadChunksPerFrame'
    )
    return { appliedFields, skippedFields, recommendations }
  }

  applyDerivedField(
    rawConfig,
    appliedFields,
    skippedFields,
    'maxImmediateOrderUploadBytes',
    recommendations.GpuMaxUploadBytes
  )
  applyDerivedField(
    rawConfig,
    appliedFields,
    skippedFields,
    'maxOrderUploadBytesPerFrame',
    recommendations.GpuMaxUploadBytes
  )
  applyDerivedField(
    rawConfig,
    appliedFields,
    skippedFields,
    'maxOrderUploadChunksPerFrame',
    recommendations.GpuMaxUploadCount
  )

  return { appliedFields, skippedFields, recommendations }
}

function applyDerivedField<K extends keyof GSplatDeviceBudgetAppliedFields>(
  rawConfig: Partial<LODConfig>,
  target: GSplatDeviceBudgetAppliedFields,
  skippedFields: string[],
  key: K,
  value: GSplatDeviceBudgetAppliedFields[K]
): void {
  if (value === undefined) {
    return
  }
  if (hasExplicitField(rawConfig, key)) {
    skippedFields.push(key)
    return
  }
  target[key] = value
}

function mergeDefined<T extends object>(base: T, patch: Partial<T>): T {
  const result = { ...base }
  for (const [key, value] of Object.entries(patch) as [keyof T, T[keyof T]][]) {
    if (value !== undefined) {
      result[key] = value
    }
  }
  return result
}

function hasExplicitField<T extends object>(obj: Partial<T>, key: keyof T): boolean {
  return Object.hasOwn(obj, key) && obj[key] !== undefined
}

function hasExplicitNumber<T extends object>(obj: Partial<T>, key: keyof T): boolean {
  return hasExplicitField(obj, key) && typeof obj[key] === 'number'
}

function normalizeTexturePixelsPerSplat(value: number | undefined): number {
  const normalized = normalizePositiveInteger(value)
  return normalized ?? DEFAULT_TEXTURE_PIXELS_PER_SPLAT
}

function normalizePositiveInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : null
}

function getNavigatorLike(): Pick<Navigator, 'userAgent' | 'hardwareConcurrency'> | null {
  return typeof navigator !== 'undefined' ? navigator : null
}

function detectOS(userAgent: string): GSplatDeviceBudgetOS {
  if (/iphone|ipad|ipod/i.test(userAgent)) return 'ios'
  if (/android/i.test(userAgent)) return 'android'
  if (/cros/i.test(userAgent)) return 'chromeos'
  if (/windows/i.test(userAgent)) return 'windows'
  if (/mac os|macintosh/i.test(userAgent)) return 'macos'
  if (/linux/i.test(userAgent)) return 'linux'
  return userAgent ? 'other' : 'unknown'
}

function detectBrowser(userAgent: string): GSplatDeviceBudgetBrowser {
  if (/firefox/i.test(userAgent)) return 'firefox'
  if (/edg\//i.test(userAgent)) return 'edge'
  if (/chrome|chromium|crios/i.test(userAgent)) return 'chrome'
  if (/safari/i.test(userAgent)) return 'safari'
  return userAgent ? 'other' : 'unknown'
}

function detectGpuVendor(renderer: string | null): GSplatDeviceBudgetGpuVendor {
  if (!renderer) return 'unknown'
  if (/nvidia|geforce|rtx|gtx/i.test(renderer)) return 'nvidia'
  if (/amd|radeon|rx \d/i.test(renderer)) return 'amd'
  if (/intel/i.test(renderer)) return 'intel'
  if (/apple/i.test(renderer)) return 'apple'
  return 'other'
}

function detectGpuModel(renderer: string | null): number | null {
  if (!renderer) return null
  const match = renderer.match(/(?:rtx|gtx|geforce|nvidia|rx|radeon|amd)[^\d]*(\d{3,4})/i)
  if (!match) return null
  return Number.parseInt(match[1], 10)
}

function detectMaxTextureSize(renderer?: WebGLRenderer): number | null {
  const gl = getRendererContext(renderer)
  if (!gl) return null
  const maxTextureSizeKey = gl.MAX_TEXTURE_SIZE ?? 0x0d33
  return normalizePositiveInteger(gl.getParameter(maxTextureSizeKey))
}

function detectGpuRenderer(renderer?: WebGLRenderer): string | null {
  const gl = getRendererContext(renderer)
  if (!gl) return null

  const debugInfo = gl.getExtension?.('WEBGL_debug_renderer_info')
  if (debugInfo?.UNMASKED_RENDERER_WEBGL) {
    const unmasked = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
    if (typeof unmasked === 'string' && unmasked.length > 0) {
      return unmasked
    }
  }

  const rendererValue = gl.getParameter(gl.RENDERER ?? 0x1f01)
  return typeof rendererValue === 'string' && rendererValue.length > 0 ? rendererValue : null
}

function getRendererContext(renderer?: WebGLRenderer): WebGLRenderingContext | null {
  const maybeRenderer = renderer as Partial<WebGLRenderer> | undefined
  if (typeof maybeRenderer?.getContext !== 'function') {
    return null
  }
  return maybeRenderer.getContext() as WebGLRenderingContext
}
